import { isDefined, isNotDefined } from '@sgme/fp';
import { useBatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useHighlightColor } from 'components/common-form/hooks/useHighlightColor';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { PercentWithUnitAndLock } from 'components/shared/Percent/PercentWithUnitAndLock';
import type { LockablePercentFieldData, LockablePercentFieldMetadata } from 'models/forms/fields';
import { FC, useCallback, useMemo, useState } from 'react';

import type { ProductNodeBuilderProps } from '../../ProductNodeBuilder.model';

export const LockablePercentField: FC<ProductNodeBuilderProps> = ({ node, isMultipleApply, tabIndex }) => {
  const [lockValue, setLockValue] = useData<LockablePercentFieldData>(node.id);
  const fieldMetadata = useFieldMetadata<LockablePercentFieldMetadata>(node.id);

  const {
    isLocked,
    showValue,
    nbDecimals,
    isHighlighted,
    decimalPadding: metaDataDecimalPadding,
  } = useMemo(() => {
    if (isNotDefined(fieldMetadata)) {
      return { isLocked: false, showValue: false, nbDecimals: 0, isHighlighted: false, decimalPadding: undefined };
    }
    const { isLocked, showValue, nbDecimals, isHighlighted, decimalPadding } = fieldMetadata;
    return { isLocked, showValue, nbDecimals, isHighlighted, decimalPadding };
  }, [fieldMetadata]);

  const {
    isCellInactive,
    onEditingKeyDown: onKeyDown,
    isFocused,
    lostInputBeforeEditing,
    isSelectAll,
    decimalPadding,
  } = useBatchNavigationContext({ decimalPadding: metaDataDecimalPadding });

  const translationKey = useFieldTranslationKey(node.id);
  const highlightColor = useHighlightColor();
  const patchedFieldClassName = usePatchedFieldHighlight(node.id);
  const [isLockedMultipleApply, setIsLockedMultipleApply] = useState<boolean>(false);
  const onBlur = useCallback(
    (val: number | undefined) => {
      const newValue = val !== undefined && Number.isFinite(val) ? val : null;

      if (newValue !== lockValue?.value) {
        setLockValue({ value: newValue, customized: true });
      }
    },
    [lockValue, setLockValue],
  );

  const onLockChanged = useCallback(
    (locked: boolean) => {
      if (isMultipleApply) {
        setIsLockedMultipleApply(locked);
      }
      if (lockValue) {
        setLockValue(locked ? null : { value: lockValue.value, customized: true });
      } else {
        setLockValue(locked ? null : { value: 0, customized: true });
      }
    },
    [lockValue, setLockValue, isMultipleApply],
  );

  const lostInputLockFieldValue = useMemo<Readonly<Nullable<LockablePercentFieldData>> | undefined>(() => {
    if (isNotDefined(lostInputBeforeEditing)) {
      return undefined;
    }
    return { value: Number(lostInputBeforeEditing), customized: lockValue?.customized } as LockablePercentFieldData;
  }, [lockValue?.customized, lostInputBeforeEditing]);

  const inputValue = useMemo<Readonly<Nullable<number>> | undefined>(() => {
    if (!showValue || !lockValue) {
      return null;
    }
    if (isDefined(lostInputLockFieldValue)) {
      return lostInputLockFieldValue.value;
    }
    return lockValue.value;
  }, [lockValue, lostInputLockFieldValue, showValue]);

  return (
    <BlockOrientationFormGroup
      inputId={node.id}
      label={`form.${translationKey}` as any}
      className={patchedFieldClassName}
    >
      <PercentWithUnitAndLock
        e2e={node.id}
        unit="%"
        value={inputValue}
        isLocked={isMultipleApply ? isLockedMultipleApply : isLocked}
        onBlur={onBlur}
        precision={nbDecimals}
        onLockChanged={onLockChanged}
        highlightColor={isHighlighted ? highlightColor : undefined}
        decimalPadding={decimalPadding}
        tabIndex={tabIndex}
        isCellInactive={isCellInactive}
        isFocused={isFocused}
        isSelectAll={isSelectAll}
        onKeyDown={onKeyDown}
        isDelegated={true}
      />
    </BlockOrientationFormGroup>
  );
};

LockablePercentField.displayName = 'LockablePercentField';
